import { useEffect, useState } from "react";
import { CustomDialog } from "@/components/dialog/v1/CustomDialog";
import { useUpdateShortLinkMutation } from "@/hooks/ReactQuery/ShortLinkReactQuery/ShortLinkReactQuery";
import { buildUrlWithUtm, EMPTY_UTM, MAX_TITLE_LENGTH, splitUtm } from "../utils";
import { FgGroupField } from "./FgGroups";
import { FgSwitch } from "./FgSwitch";
import { FgUtmFields } from "./FgUtmFields";

export function MdShortLinkEdit({ shortLink, groups, onClose }) {
    const updateShortLinkMutation = useUpdateShortLinkMutation();

    const [title, setTitle] = useState('');
    const [group, setGroup] = useState('');
    const [url, setUrl] = useState('');
    const [utm, setUtm] = useState(EMPTY_UTM);
    const [openInApp, setOpenInApp] = useState(true);

    // 열 때마다 저장된 값으로 채운다 (UTM 은 원본 링크와 분리해서 보여줌)
    useEffect(() => {
        if (!shortLink) {
            return;
        }

        const { base, utm } = splitUtm(shortLink.url);
        setTitle(shortLink.title || '');
        setGroup(shortLink.group || '');
        setUrl(base);
        setUtm(utm);
        setOpenInApp(shortLink.openInApp);
    }, [shortLink]);

    const finalUrl = url.trim() ? buildUrlWithUtm(url, utm) : null;
    const urlChanged = !!shortLink && !!finalUrl && finalUrl !== shortLink.url;

    const handleSubmit = (e) => {
        e.preventDefault();

        updateShortLinkMutation.mutate({ id: shortLink.id, body: { title, group, url: finalUrl, openInApp } }, {
            onSuccess: () => onClose(),
        });
    }

    return (
        <CustomDialog open={!!shortLink} onClose={onClose} maxWidth="sm">
            <CustomDialog.CloseButton onClose={onClose} />
            {shortLink &&
                <form onSubmit={handleSubmit} className="px-6 pb-6 flex flex-col gap-4">
                    <div>
                        <h3 className="text-lg font-bold">단축 URL 수정</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{shortLink.shortUrl} · 주소와 클릭 기록은 그대로 유지됩니다.</p>
                    </div>

                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-gray-600">라벨 <span className="font-normal text-gray-400">선택</span></span>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={MAX_TITLE_LENGTH}
                            placeholder="예: 10월 가을 세일 - 인스타 바이오"
                            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[var(--mainColor)]"
                        />
                    </label>

                    <FgGroupField value={group} onChange={setGroup} groups={groups} />

                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-gray-600">원본 링크</span>
                        <input
                            type="url"
                            required
                            maxLength={2048}
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[var(--mainColor)]"
                        />
                    </label>

                    <FgUtmFields utm={utm} onChange={setUtm} finalUrl={finalUrl} defaultOpen={Object.values(splitUtm(shortLink.url).utm).some(Boolean)} key={shortLink.id} />

                    {urlChanged &&
                        <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                            목적지가 바뀝니다. 이미 공유한 단축 URL·QR 도 저장 즉시 새 링크로 이동합니다.
                        </p>
                    }

                    <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold">인앱 브라우저에서 앱으로 열기</p>
                        <FgSwitch checked={openInApp} onChange={setOpenInApp} label="인앱 브라우저에서 앱으로 열기" />
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-1">
                        <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 py-2.5 text-sm font-semibold">취소</button>
                        <button type="submit" disabled={!finalUrl} className="rounded-lg bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 py-2.5 text-sm font-semibold text-white">저장</button>
                    </div>
                </form>
            }
        </CustomDialog>
    );
}
